import React from "react";
import styled from "styled-components";
import {
    ShoppingCart,
    Restaurant,
    LocalShipping,
    DirectionsBike,
    Home,
    CheckCircle,
    Cancel,
} from "@material-ui/icons";
import { mobile } from "../../responsive";

const BRAUN = {
    surface: "#ffffff",
    text: "#1f1f1f",
    textMuted: "#6e6e6e",
    border: "#d8d8d5",
    accent: "#2f2f2f",
    accentSoft: "#e9e9e6",
    warm: "#b08d57",
    success: "#2f7d32",
    danger: "#b42318",
};

// Status progression order
const STEPS = [
    { key: "PROCESSING", label: "Order Placed", icon: ShoppingCart },
    { key: "PENDING_3PL", label: "Preparing", icon: Restaurant },
    { key: "ASSIGNED", label: "Ready", icon: LocalShipping },
    { key: "PICKED_UP", label: "Picked Up", icon: DirectionsBike },
    { key: "DISPATCHED", label: "On the Way", icon: DirectionsBike },
    { key: "DELIVERED", label: "Delivered", icon: Home },
];

// Get step index for current status
const getStepIndex = (status) => {
    // ACCEPTED is between ASSIGNED and PICKED_UP
    if (status === "ACCEPTED") return 2.5;
    // Rider-leg statuses without their own step: both mean the order is moving
    // — light everything up to "On the Way" (ARRIVED is at the doorstep, just
    // shy of Delivered) instead of falling back to step 0 "Order Placed".
    if (status === "IN_TRANSIT") return 4;
    if (status === "ARRIVED") return 4.5;
    const index = STEPS.findIndex((step) => step.key === status);
    return index >= 0 ? index : 0;
};

// Styled Components
const StepperContainer = styled.div`
  background: linear-gradient(180deg, ${BRAUN.surface} 0%, #f8f8f7 100%);
  border-radius: 12px;
  padding: 20px 18px;
  margin-bottom: 16px;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.06);
  border: 1px solid ${BRAUN.border};
  border-top: 3px solid ${BRAUN.warm};

  ${mobile({
    padding: "14px 12px",
    overflow: "hidden",
})}
`;

const StepperTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${BRAUN.text};
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: 0.2px;

  svg {
    color: ${BRAUN.warm};
    font-size: 18px;
  }
`;

const StepsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;

  ${mobile({
    display: "grid",
    gridTemplateColumns: "1fr",
    rowGap: "12px",
})}
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
  z-index: 1;

  ${mobile({
    display: "grid",
    gridTemplateColumns: "36px 1fr",
    columnGap: "12px",
    alignItems: "center",
    width: "100%",
    padding: "0",
    flex: "none",
})}
`;

const StepCircle = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) =>
        props.completed
            ? BRAUN.success
            : props.active
                ? BRAUN.accent
                : props.rejected
                    ? BRAUN.danger
                    : BRAUN.accentSoft};
  color: ${(props) =>
        props.completed || props.active || props.rejected ? "#ffffff" : "#7a7a76"};
  transition: all 0.3s ease;
  box-shadow: ${(props) =>
        props.active
            ? "0 0 0 4px rgba(176, 141, 87, 0.25)"
            : props.completed
                ? "0 4px 10px rgba(47, 125, 50, 0.28)"
                : "none"};

  svg {
    font-size: 22px;
  }

  ${mobile({
    width: "36px",
    height: "36px",
    flexShrink: 0,
})}
`;

const StepContent = styled.div`
  text-align: center;
  margin-top: 10px;

  ${mobile({
    textAlign: "left",
    marginTop: 0,
    marginLeft: 0,
    minWidth: 0,
})}
`;

const StepLabel = styled.div`
  font-size: 11px;
  font-weight: ${(props) => (props.active ? 700 : 500)};
  color: ${(props) =>
        props.completed
            ? BRAUN.success
            : props.active
                ? BRAUN.accent
                : props.rejected
                    ? BRAUN.danger
                    : BRAUN.textMuted};
  margin-bottom: 2px;
  white-space: nowrap;

  ${mobile({
                        fontSize: "13px",
                    })}
`;

const StepStatus = styled.div`
  font-size: 10px;
  color: ${(props) => (props.completed ? BRAUN.success : "#9d9d98")};
  font-weight: 500;

  ${mobile({
    fontSize: "11px",
})}
`;

const ConnectorLine = styled.div`
  position: absolute;
  top: 22px;
  left: calc(50% + 22px);
  right: calc(-50% + 22px);
  height: 3px;
  background: ${(props) =>
        props.completed
            ? `linear-gradient(90deg, ${BRAUN.warm}, ${BRAUN.success})`
            : "#dcdcd8"};
  z-index: 0;
  transition: background 0.3s ease;

  ${mobile({
                display: "none",
            })}
`;

const RejectedBadge = styled.div`
  background: #fef3f2;
  color: ${BRAUN.danger};
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  border: 1px solid #fecaca;

  svg {
    font-size: 18px;
  }
`;

const OrderProgressStepper = ({
    currentStatus = "PROCESSING",
    isPaid = false,
    isPackageDelivery = false
}) => {
    const isRejected = currentStatus === "REJECTED";
    const currentStepIndex = getStepIndex(currentStatus);

    // Adjust steps for package delivery (skip "Preparing" step)
    const displaySteps = isPackageDelivery
        ? STEPS.filter((step) => step.key !== "PENDING_3PL")
        : STEPS;

    return (
        <StepperContainer>
            <StepperTitle>
                <LocalShipping />
                Order Progress
            </StepperTitle>

            <StepsWrapper>
                {displaySteps.map((step, index) => {
                    const stepIndex = getStepIndex(step.key);
                    const isCompleted = stepIndex < currentStepIndex;
                    const isActive = step.key === currentStatus ||
                        (currentStatus === "ACCEPTED" && step.key === "ASSIGNED");
                    const IconComponent = step.icon;

                    return (
                        <React.Fragment key={step.key}>
                            <Step>
                                <StepCircle
                                    completed={isCompleted}
                                    active={isActive}
                                    rejected={isRejected && isActive}
                                >
                                    {isCompleted ? (
                                        <CheckCircle />
                                    ) : isRejected && isActive ? (
                                        <Cancel />
                                    ) : (
                                        <IconComponent />
                                    )}
                                </StepCircle>
                                <StepContent>
                                    <StepLabel
                                        completed={isCompleted}
                                        active={isActive}
                                        rejected={isRejected && isActive}
                                    >
                                        {step.label}
                                    </StepLabel>
                                    <StepStatus completed={isCompleted}>
                                        {isCompleted ? "✓ Done" : isActive ? "Current" : "Pending"}
                                    </StepStatus>
                                </StepContent>
                                {index < displaySteps.length - 1 && (
                                    <ConnectorLine completed={isCompleted} />
                                )}
                            </Step>
                        </React.Fragment>
                    );
                })}
            </StepsWrapper>

            {isRejected && (
                <RejectedBadge>
                    <Cancel />
                    This order was cancelled
                </RejectedBadge>
            )}
        </StepperContainer>
    );
};

export default OrderProgressStepper;
